import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, FileText, FileCheck, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { correctionApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errorUtils';

const eur = (v) => (v ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

export const CORRECTION_STATUS_LABELS = {
  draft: 'Bozza', pending_signature: 'Da firmare', signed: 'Firmata',
  fiscal_hold: 'Da regolarizzare', effective: 'Effettiva', cancelled: 'Annullata',
};
export const CORRECTION_STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-600', pending_signature: 'bg-amber-100 text-amber-700',
  signed: 'bg-blue-100 text-blue-700', fiscal_hold: 'bg-red-100 text-red-700',
  effective: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-slate-100 text-slate-400',
};

const openBlob = (data) => {
  const url = URL.createObjectURL(data);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const CorrectionDetailDialog = ({ open, onOpenChange, correctionId, originalExtra, isAdmin = false, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [corr, setCorr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!correctionId) return;
    setLoading(true);
    try { const r = await correctionApi.getOne(correctionId); setCorr(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open && correctionId) { setConfirmApply(false); load(); } }, [open, correctionId]);

  const refreshAll = async () => { await load(); onUpdated && onUpdated(); };

  const doGenerate = async () => {
    setBusy(true);
    try { const r = await correctionApi.generatePdf(correctionId); openBlob(r.data); toast.success('Documento generato'); await refreshAll(); }
    catch (e) { toast.error(getErrorMessage(e, 'Errore generazione documento')); }
    finally { setBusy(false); }
  };
  const doViewSigned = async () => {
    try { const r = await correctionApi.signedPdf(correctionId); openBlob(r.data); }
    catch (e) { toast.error(getErrorMessage(e, 'Documento firmato non disponibile')); }
  };
  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await correctionApi.uploadSigned(correctionId, file); toast.success('Documento firmato caricato'); await refreshAll(); }
    catch (e) { toast.error(getErrorMessage(e, 'Errore caricamento documento firmato')); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const doApply = async () => {
    setBusy(true);
    try {
      const r = await correctionApi.applyEffective(correctionId);
      const st = r.data?.status;
      toast.success(st === 'fiscal_hold' ? 'Rettifica in FISCAL HOLD (nessun effetto economico)' : 'Rettifica resa effettiva');
      setConfirmApply(false);
      await refreshAll();
    } catch (e) { toast.error(getErrorMessage(e, "Errore nell'applicazione della rettifica")); }
    finally { setBusy(false); }
  };

  const originalNet = originalExtra?.corrections_summary?.original_net ?? originalExtra?.total ?? 0;
  const st = corr?.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="correction-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#171717]">{corr?.correction_number || 'Rettifica'}</DialogTitle>
        </DialogHeader>
        {loading || !corr ? (
          <div className="py-10 flex items-center justify-center text-[#737373]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${CORRECTION_STATUS_TONE[st]}`} data-testid="correction-detail-status">{CORRECTION_STATUS_LABELS[st] || st}</span>
              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{corr.correction_type === 'full' ? 'Totale' : 'Parziale'}</span>
            </div>

            <div className="rounded-lg border border-[#ECECEC] p-3 space-y-1">
              <div className="flex justify-between"><span className="text-[#737373]">Extra originale</span><span>{corr.original_extra_number}</span></div>
              <div className="flex justify-between"><span className="text-[#737373]">Creata il</span><span>{(corr.created_at || '').slice(0, 10)}</span></div>
              {corr.signed_at && <div className="flex justify-between"><span className="text-[#737373]">Firmata il</span><span>{corr.signed_at.slice(0, 10)}</span></div>}
              {corr.effective_at && <div className="flex justify-between"><span className="text-[#737373]">Effettiva dal</span><span>{corr.effective_at.slice(0, 10)}</span></div>}
              <div><span className="text-[#737373]">Motivo</span><div className="mt-0.5">{corr.reason}</div></div>
            </div>

            {(corr.items || []).length > 0 && (
              <div className="rounded-lg border border-[#ECECEC] overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA] text-[#737373]">
                    <tr><th className="text-left p-2">Codice</th><th className="text-left p-2">Descrizione</th><th className="text-right p-2">Q.tà</th><th className="text-right p-2">P.U.</th><th className="text-right p-2">IVA</th><th className="text-right p-2">Netto</th></tr>
                  </thead>
                  <tbody>
                    {corr.items.map((it, i) => (
                      <tr key={i} className="border-t border-[#F0F0F0]">
                        <td className="p-2">{it.original_item_code}</td>
                        <td className="p-2">{it.description}</td>
                        <td className="p-2 text-right tabular-nums">{it.quantity_storno} {it.unit}</td>
                        <td className="p-2 text-right tabular-nums">{eur(it.unit_price)}</td>
                        <td className="p-2 text-right tabular-nums">{it.vat_percentage}%</td>
                        <td className="p-2 text-right tabular-nums text-red-600">{eur(it.net_delta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-lg border border-[#ECECEC] p-3 space-y-1">
              <div className="flex justify-between"><span className="text-[#737373]">Valore Extra originario</span><span className="tabular-nums">{eur(originalNet)}</span></div>
              <div className="flex justify-between text-red-600 font-medium"><span>Rettifica (netto)</span><span className="tabular-nums">{eur(corr.net_delta)}</span></div>
              <div className="flex justify-between border-t border-dashed border-[#ECECEC] pt-1 font-semibold"><span>{st === 'effective' ? 'Valore residuo' : 'Valore residuo previsto'}</span><span className="tabular-nums">{eur(originalNet + (corr.net_delta || 0))}</span></div>
            </div>

            {/* Documenti */}
            {(st === 'pending_signature' || st === 'signed' || st === 'fiscal_hold' || st === 'effective') && (
              <div className="rounded-lg border border-[#ECECEC] p-3 space-y-2" data-testid="correction-documents">
                <div className="text-xs font-semibold text-[#737373]">Documenti</div>
                <Button variant="outline" size="sm" onClick={doGenerate} disabled={busy} data-testid="correction-view-generated">
                  <FileText className="w-4 h-4 mr-1" /> Documento rettifica
                </Button>
                {corr.signed_pdf_filename && (
                  <Button variant="outline" size="sm" onClick={doViewSigned} className="ml-2" data-testid="correction-view-signed">
                    <FileCheck className="w-4 h-4 mr-1" /> Documento firmato
                  </Button>
                )}
              </div>
            )}

            {/* Immutabilità firmata */}
            {(st === 'signed' || st === 'fiscal_hold' || st === 'effective') && (
              <div className="text-[11px] text-[#8a8a8a] flex items-center gap-1" data-testid="correction-immutable-note">
                <AlertTriangle className="w-3 h-3" /> Rettifica firmata: documento e dati non modificabili.
              </div>
            )}
            {st === 'fiscal_hold' && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700" data-testid="correction-fiscal-hold-note">
                <strong>Rettifica firmata · da regolarizzare.</strong> Nessun effetto economico applicato finché la posizione fiscale non verrà regolarizzata.
              </div>
            )}
            {st === 'effective' && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-700 flex items-center gap-1" data-testid="correction-effective-note">
                <CheckCircle2 className="w-4 h-4" /> Rettifica effettiva.
              </div>
            )}
            {st !== 'effective' && st !== 'fiscal_hold' && (
              <div className="text-[11px] text-[#8a8a8a]">Questa rettifica non è ancora effettiva: non modifica il valore corrente dell'Extra.</div>
            )}

            {/* AZIONI LIFECYCLE (Admin) */}
            {isAdmin && (
              <div className="border-t pt-3 flex flex-wrap items-center gap-2" data-testid="correction-actions">
                {st === 'draft' && (
                  <Button onClick={doGenerate} disabled={busy} data-testid="correction-generate-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Genera documento
                  </Button>
                )}
                {st === 'pending_signature' && (
                  <>
                    <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
                      data-testid="correction-upload-input" onChange={(e) => doUpload(e.target.files?.[0])} />
                    <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="correction-upload-btn">
                      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Carica documento firmato
                    </Button>
                  </>
                )}
                {st === 'signed' && !confirmApply && (
                  <Button onClick={() => setConfirmApply(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="correction-apply-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Rendi effettiva la rettifica
                  </Button>
                )}
                {st === 'signed' && confirmApply && (
                  <div className="w-full rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="correction-apply-confirm">
                    <div className="text-xs text-amber-900">
                      Questa operazione modificherà il valore corrente dell'Extra e del contratto e non potrà essere annullata. Il documento originale dell'Extra rimarrà invariato.
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setConfirmApply(false)} disabled={busy}>Annulla</Button>
                      <Button size="sm" onClick={doApply} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="correction-apply-confirm-btn">
                        {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Rendi effettiva
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CorrectionDetailDialog;
